import { useRef, useState, useCallback, useEffect, useMemo, MutableRefObject, RefObject } from 'react';
import ScrollCue, { ScrollCueProps } from './scroll-cue.component';
import { createScrollCueStore } from './scroll-cue.store';

interface UseHasScrollParams<T extends HTMLElement> {
  externalElRef?: RefObject<T>;
  key?: any;
  /**
   * Set this when the element being tracked (`elRef`) doesn't actually clip/scroll its own
   * overflow (e.g. it has `minHeight`/`height: auto` and just grows with its content).
   * In that case the window/document is what actually scrolls, so scroll position,
   * scroll distance, and "has more content" calculations need to be based on the window
   * instead of the tracked element.
   */
  useWindowScroll?: boolean;
}

interface UseHasScrollResults<T extends HTMLElement> {
  elRef: RefObject<T>;
  hasScroll: boolean;
  ScrollCue: typeof ScrollCue;
  scrollCueProps: ScrollCueProps;
}

const APPEARANCE_DELAY = 3000;
const DEBOUNCE_DELAY = 100;
const CLICK_SCROLL_GUARD_DELAY = 500;

type TimerRef = MutableRefObject<ReturnType<typeof setTimeout> | null>;

const clearTimer = (timerRef: TimerRef) => {
  if (timerRef.current) {
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }
};

export const useHasScroll = <T extends HTMLElement = HTMLElement>({
  externalElRef,
  key,
  useWindowScroll = false,
}: UseHasScrollParams<T> = {}): UseHasScrollResults<T> => {
  const [hasScroll, setHasScroll] = useState(false);
  // Scroll-driven state lives outside React so it never re-renders the host (e.g. the whole form).
  const [store] = useState(createScrollCueStore);
  const isScrollingByClick = useRef(false);

  const internalElRef = useRef<T>(null);
  const elRef = externalElRef ? externalElRef : internalElRef;

  const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isReadyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const elHeightRef = useRef(0);
  const observerRef = useRef<ResizeObserver | null>(null);
  const observedElRef = useRef<T | null>(null);
  const resizeInProgressRef = useRef(false);
  const heightChangeInProgressRef = useRef(false);

  const clearCueTimeout = useCallback(() => clearTimer(isReadyTimeoutRef), []);

  const showCueAfterDelay = useCallback(() => {
    // Restart rather than stack, so repeated calls can't leave orphaned timers behind.
    clearTimer(isReadyTimeoutRef);
    isReadyTimeoutRef.current = setTimeout(() => store.setState({ isReady: true }), APPEARANCE_DELAY);
  }, [store]);

  /**
   * Returns the scroll metrics for whichever "scrollable" is actually in play: the tracked
   * element, or (when `useWindowScroll` is set) the window/document.
   */
  const getScrollMetrics = useCallback(() => {
    if (useWindowScroll) {
      const scrollingElement = document.scrollingElement ?? document.documentElement;

      return {
        scrollHeight: scrollingElement?.scrollHeight ?? 0,
        clientHeight: window.innerHeight ?? 0,
        scrollTop: window.scrollY ?? 0,
      };
    }

    return {
      scrollHeight: elRef.current?.scrollHeight ?? 0,
      clientHeight: elRef.current?.clientHeight ?? 0,
      scrollTop: elRef.current?.scrollTop ?? 0,
    };
  }, [elRef, useWindowScroll]);

  const scrollEventHandler = useCallback(() => {
    if (resizeInProgressRef.current || heightChangeInProgressRef.current) {
      return;
    }

    clearTimer(scrollTimeoutRef);

    if (!isScrollingByClick.current) {
      /**
       * If the user is scrolling by not clicking the scroll icon,
       * don't show the cue. Once the user stops scrolling, show the cue after
       * a delay.
       */
      clearCueTimeout();
      store.setState({ isScrolling: true, isReady: false });
    }

    scrollTimeoutRef.current = setTimeout(() => {
      if (!useWindowScroll && !elRef.current) {
        return;
      }

      const { scrollHeight, clientHeight, scrollTop } = getScrollMetrics();
      const scrolledTo = scrollTop + clientHeight;
      const bottomPos = scrolledTo > scrollHeight ? Math.floor(scrolledTo) : Math.round(scrolledTo);

      store.setState({ hasMoreContent: scrollHeight > clientHeight && bottomPos !== scrollHeight });

      if (!isScrollingByClick.current) {
        store.setState({ isScrolling: false });
        showCueAfterDelay();
      }
    }, DEBOUNCE_DELAY);
  }, [clearCueTimeout, elRef, getScrollMetrics, showCueAfterDelay, store, useWindowScroll]);

  const resizeEventHandler = useCallback(() => {
    clearTimer(resizeTimeoutRef);

    resizeInProgressRef.current = true;
    resizeTimeoutRef.current = setTimeout(() => {
      const { scrollHeight, clientHeight: elHeight } = getScrollMetrics();

      if (scrollHeight > elHeight) {
        setHasScroll(true);
      } else {
        setHasScroll(false);
      }

      resizeInProgressRef.current = false;
      scrollEventHandler();
    }, DEBOUNCE_DELAY);
  }, [getScrollMetrics, scrollEventHandler]);

  const scrollIconClickHandler = useCallback(() => {
    if (!useWindowScroll && !elRef.current) {
      return;
    }

    const { clientHeight } = getScrollMetrics();

    let scrollDistance = clientHeight / 4;

    /**
     * If the phone is in landscape mode, scroll a bit more.
     */
    if (scrollDistance < 200) {
      scrollDistance = clientHeight / 2;
    }

    isScrollingByClick.current = true;

    if (useWindowScroll) {
      window.scrollBy({ behavior: 'smooth', top: scrollDistance });
    } else {
      elRef.current?.scrollBy({ behavior: 'smooth', top: scrollDistance });
    }

    clearTimer(clickTimeoutRef);
    clickTimeoutRef.current = setTimeout(() => {
      isScrollingByClick.current = false;
    }, CLICK_SCROLL_GUARD_DELAY);
  }, [elRef, getScrollMetrics, useWindowScroll]);

  useEffect(() => {
    resizeEventHandler();
  }, [resizeEventHandler, key]);

  /**
   * Adds resize event listener
   */
  useEffect(() => {
    window.addEventListener('resize', resizeEventHandler);

    return () => {
      window.removeEventListener('resize', resizeEventHandler);
    };
  }, [resizeEventHandler]);

  /**
   * Detects height change of the tracked element. Even when relying on window scroll, the
   * tracked element grows with its content (it isn't clipped), so its height is still a valid
   * signal that the available content has changed.
   *
   * Runs after every render but only does work when the element (un)mounts, since it can mount
   * after the hook (e.g. behind a loading state).
   */
  const resizeHandlerRef = useRef(resizeEventHandler);

  useEffect(() => {
    resizeHandlerRef.current = resizeEventHandler;

    const el = elRef.current;

    if (el === observedElRef.current) {
      return;
    }

    observerRef.current?.disconnect();
    observerRef.current = null;
    observedElRef.current = el;

    if (!el || typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];

      if (!entry) {
        return;
      }

      const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;

      if (height === elHeightRef.current) {
        return;
      }

      elHeightRef.current = height;
      heightChangeInProgressRef.current = true;
      clearTimer(heightTimeoutRef);
      heightTimeoutRef.current = setTimeout(() => {
        heightChangeInProgressRef.current = false;
        resizeHandlerRef.current();
      }, DEBOUNCE_DELAY);
    });

    observer.observe(el);
    observerRef.current = observer;
  });

  /**
   * Adds scroll event listener, either to the tracked element, or the window when the tracked
   * element doesn't scroll on its own.
   */
  useEffect(() => {
    const target: Window | T | null = useWindowScroll ? window : elRef.current;
    target?.addEventListener('scroll', scrollEventHandler, { passive: true });

    return () => {
      target?.removeEventListener('scroll', scrollEventHandler);
    };
  }, [elRef, scrollEventHandler, useWindowScroll]);

  /**
   * Readies the scroll cue to show up (if there is more content) after 3s,
   * and clears every pending timer on unmount.
   */
  useEffect(() => {
    showCueAfterDelay();

    return () => {
      [resizeTimeoutRef, scrollTimeoutRef, heightTimeoutRef, isReadyTimeoutRef, clickTimeoutRef].forEach(clearTimer);
      observerRef.current?.disconnect();
      observerRef.current = null;
      observedElRef.current = null;
      resizeInProgressRef.current = false;
      heightChangeInProgressRef.current = false;
      isScrollingByClick.current = false;
    };
  }, [showCueAfterDelay]);

  const scrollCueProps = useMemo<ScrollCueProps>(
    () => ({ store, onClickScrollIcon: scrollIconClickHandler }),
    [store, scrollIconClickHandler]
  );

  return { elRef, hasScroll, ScrollCue, scrollCueProps };
};
